/**
 * useRowDeleteTargets
 *
 * Owns both halves of a row-anchored Delete for ArRowActions and ArRowContextMenu:
 * what it deletes and whether it is allowed. A non-empty selection always wins over
 * the clicked row, as Cmd+Backspace does. Every target goes through `deleteRows` —
 * even a single clicked row — because the trash an undo entry addresses is written
 * by `arTableRowOperations` and by nothing else, and a client-side document delete
 * would leave the commonest delete in the product unrecoverable (SYD-2447). Targets
 * are captured at menu-open time from the bare proxy: subscribing every row to
 * `selection` through useSnapshot would re-render the whole table on each selection
 * click.
 *
 * Delete asks nothing before it runs. It applies immediately and the undo buttons
 * carry the way back, which is the trade a confirmation cannot make: it stops every
 * delete, while undo costs nothing until the user wants it. The result message is
 * therefore the only thing that can report a refusal, so every path emits one
 * through `reportRowDeleteResult`.
 *
 * "Non-empty selection" means the SCOPED selection
 * (`resolveSelectionScope().selectedVisibleRowIds`): a tick the current split,
 * filter or view has hidden is not a delete target, and a selection narrowed away
 * entirely falls back to the clicked row (SYD-2386).
 *
 * A row-anchored Delete acts on those visible ids even while the selection scope
 * is `'all'` (SYD-2386, S13). That is deliberate: the whole-table delete is
 * reachable only from the Actions menu, where the escalation was made and where
 * the scope-aware confirmation lives.
 *
 * A row a Form Derive projection owns cannot be deleted from here at all: the
 * reconciler would rebuild it on the next pass, so the clicked-row Delete is
 * disabled with the projection's own reason, and every path leans on
 * `arTableRowOperations`, which skips those ids server-side and counts them among
 * `skipped`. Subscribing to `deriveProjections` is the one useSnapshot this hook
 * allows itself — the array moves only when the form type's config changes
 * (SYD-2404).
 *
 * The clicked-row gate stays a client-side advice on top of the server's refusal,
 * deliberately: it asks a live projection, because the answer has to quote that
 * projection's own sentence, while a bulk skip is worded from the `drv~` namespace
 * — the test the server refused on.
 */

import { useContext, useState } from 'react';
import { useSnapshot } from 'valtio';
import { ArTableActionsContext, ArTableUIStateContext } from '../context';
import { deriveProjectionOfRow } from '../derives';
import { deselectRow, resolveSelectionScope } from '../selection';
import { reportRowDeleteResult } from './reportRowDeleteResult';
import { MessageContext } from '@/providers/MessageContext';
import type { ArRowId } from '#types/System.Artifacts.types';

type DeleteTargets = {
  source: 'selection' | 'row';
  ids: ArRowId[];
};

const useRowDeleteTargets = (rowId: ArRowId, isLocked: boolean) => {
  const uiState = useContext(ArTableUIStateContext)!;
  const actions = useContext(ArTableActionsContext)!;
  const { messageApi } = useContext(MessageContext);
  const [targets, setTargets] = useState<DeleteTargets>({ source: 'row', ids: [rowId] });
  const deriveProjectionsSnap = useSnapshot(uiState.deriveProjections);
  const rowDeriveProjection = deriveProjectionOfRow(deriveProjectionsSnap, rowId);

  const capture = (open: boolean) => {
    if (!open) return;
    const { selectedVisibleRowIds: selected } = resolveSelectionScope({
      selectableRowIds: uiState.selectableRowIds,
      selectedRows: uiState.selection.selectedRows,
      scope: uiState.selection.scope,
    });
    setTargets(
      selected.length > 0
        ? { source: 'selection', ids: selected }
        : { source: 'row', ids: [rowId] }
    );
  };

  const deleteRows = async () => {
    uiState.cellEditing.editingCellsByKey = {};
    uiState.bulkEditPreview.byColumnId = {};

    const rowIds = targets.ids;
    for (const id of rowIds) {
      deselectRow(uiState.selection, id);
    }

    const result = await actions.deleteRows({ rowIds });
    reportRowDeleteResult(messageApi, result, rowIds);
  };

  const count = targets.ids.length;

  return {
    label: count > 1 ? 'Delete Rows' : 'Delete Row',
    disabled:
      targets.source === 'row' && (isLocked || rowDeriveProjection !== null),
    /**
     * Why the clicked-row Delete is off, when the reason is a projection. A lock
     * needs no sentence — the row already renders as locked — but a managed row
     * looks ordinary apart from its gutter rule, so it says where to act instead.
     */
    disabledReason:
      targets.source === 'row' && !isLocked && rowDeriveProjection !== null
        ? rowDeriveProjection.rowDeleteBlocked
        : null,
    capture,
    deleteRows,
  };
};

export { useRowDeleteTargets };
